import React, { useState } from 'react';
import { api } from '../services/api';

/**
 * Manual welcome-email trigger for one client.
 *
 * "Send to default admin" resets the tenant admin's password to a fresh
 * temporary one and emails them the access details — a deliberate, confirmed
 * action. "Send test email" delivers the identical email with placeholder
 * credentials to a reviewer-chosen address and changes nothing, so the copy
 * and rendering can be checked before the real send.
 */
export default function WelcomeEmailPanel({
  tenantId,
  clientName
}: {
  tenantId: string;
  clientName: string;
}) {
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingReal, setSendingReal] = useState(false);
  const [confirmingReal, setConfirmingReal] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isValidTestEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail.trim());
  const busy = sendingTest || sendingReal;

  const handleSendTest = async () => {
    setSendingTest(true);
    setNotice(null);
    setError(null);
    try {
      const res = await api.sendWelcomeEmail(tenantId, { testEmail: testEmail.trim() });
      setNotice(res?.message ?? `Test welcome email sent to ${testEmail.trim()}.`);
    } catch (requestError: any) {
      setError(requestError?.message || 'Could not send the test email.');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendReal = async () => {
    setSendingReal(true);
    setNotice(null);
    setError(null);
    try {
      const res = await api.sendWelcomeEmail(tenantId, {});
      setNotice(res?.message ?? 'Welcome email sent to the default admin.');
      setConfirmingReal(false);
    } catch (requestError: any) {
      setError(requestError?.message || 'Could not send the welcome email.');
    } finally {
      setSendingReal(false);
    }
  };

  return (
    <section className="section">
      <div className="section-head">
        <div>
          <h2>Welcome email</h2>
          <p className="desc">
            Access details and app downloads for {clientName}. Sending to the default admin
            resets their password to a new temporary one; send a test to yourself first.
          </p>
        </div>
      </div>

      {notice && <div className="panel-note pos">{notice}</div>}
      {error && <div className="panel-note neg">{error}</div>}

      <div className="user-form-grid">
        <label className="field">
          <span>Test recipient</span>
          <input
            type="email"
            placeholder="you@apexialabs.ai"
            value={testEmail}
            onChange={(event) => setTestEmail(event.target.value)}
            disabled={busy}
          />
        </label>
      </div>

      <div className="user-form-actions">
        <button
          type="button"
          className="btn"
          onClick={handleSendTest}
          disabled={busy || !isValidTestEmail}
        >
          {sendingTest ? 'Sending test…' : 'Send test email'}
        </button>

        {!confirmingReal ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setConfirmingReal(true)}
            disabled={busy}
          >
            Send to default admin…
          </button>
        ) : (
          <span className="row-confirm">
            <span className="caption">Resets the admin&apos;s password. Send?</span>
            <button type="button" className="btn btn-danger" onClick={handleSendReal} disabled={busy}>
              {sendingReal ? 'Sending…' : 'Yes, send it'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setConfirmingReal(false)}
              disabled={busy}
            >
              Cancel
            </button>
          </span>
        )}
      </div>
    </section>
  );
}
